import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HumanReviewQueueSection } from "@/features/patient/human-review/components/HumanReviewQueueView";
import { ReviewItemView } from "@/features/patient/medications/components/ReviewItemView";
import type { ReviewItem, ReviewProcessing } from "@/portals/patient/api/reviewItemsApi";
import type { ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";
import {
  invalidatePatientData,
  invalidateRecords,
  isReviewProcessingBusy,
  patientKeys,
  reviewCountQuery,
  reviewListQuery,
  reviewProcessingQuery,
  useSubmitReviewResponse,
} from "@/portals/patient/api/patientQueries";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useProfileFromQuery } from "@/portals/patient/hooks/useProfileFromQuery";
import { AppModal } from "@/shared/components/dialog";
import { PageSpinner } from "@/shared/components/spinner";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { bulkReviewPath, countAwaiting, itemsForQueue, type ReviewQueue } from "@/features/patient/human-review/reviewQueues";

export const SKIPPED_MESSAGE = "Sent to your clinician without an answer.";

/** The review modal's heading, per kind of item. */
const MODAL_COPY: Partial<Record<ReviewTriggerType, { title: string; description: string }>> = {
  IDENTITY_MISMATCH: { title: "Identity check", description: "Compare the details and tell us whether this record is about you." },
  GOLDEN_RECORD_CONFLICT: { title: "Record check", description: "Tell your clinician which one you think is right. They make the final decision." },
};
const MEDICATION_COPY = { title: "Medication review", description: "Tell us about this medicine so your clinician can confirm it." };

/** How often the processing banner re-checks while an upload is being worked on. */
export { PROCESSING_POLL_MS } from "@/portals/patient/api/patientQueries";
/** How long the "your new records are checked" summary stays once the work finishes. */
export const FINISHED_SUMMARY_MS = 5_000;

/**
 * /patient/human-review-queue — the patient's health review items (medicines,
 * medicine pairs, record conflicts); /patient/patient-only-queue — the same
 * page for identity checks only ("Is this upload yours?", patient-only). Review
 * opens the review modal (answer and/or message → clinician); Skip sends the
 * item on without an answer. Both land in the clinician / shared queue and
 * the admin's view.
 *
 * Data comes from the shared query cache: coming back to the page shows the
 * last queue and progress at once (then revalidates quietly), and every
 * change — the patient's own answers (applied instantly, rolled back if
 * refused), notifications, uploads finishing — updates it in place.
 */
export function HumanReviewQueuePage({ queue = "health" }: { queue?: ReviewQueue }) {
  useProfileFromQuery();
  const navigate = useNavigate();
  // ?item= — a notification (e.g. the clinician's question) opens that item selected.
  const [searchParams] = useSearchParams();
  const focusItemId = searchParams.get("item");
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const client = useQueryClient();

  // One shared list; each queue shows only its own items.
  const selectQueue = useCallback((all: ReviewItem[]) => itemsForQueue(all, queue), [queue]);
  const list = useQuery({ ...reviewListQuery(actingAsProfileId), select: selectQueue });
  const items = list.data ?? null;
  const error = list.isError && !items ? (list.error as { message?: string })?.message || "Couldn't load your review queue. Please try again later." : null;
  const [openReviewId, setOpenReviewId] = useState<string | null>(null);
  const submit = useSubmitReviewResponse(actingAsProfileId);
  const skippingId = submit.isPending && submit.variables?.answer === "SKIPPED" ? submit.variables.itemId : null;

  // New records still being read / matched / compared: the progress card
  // (polled while busy). When the work finishes, the new review items load
  // FIRST and the card switches to "done" together with them — never "done,
  // 0 to review" while the list is still the old one (the card keeps its
  // last step meanwhile) — then shows the outcome for a moment instead of
  // just vanishing. Leaving mid-way and coming back after it finished still
  // shows that summary: the cache remembers the last (busy) state.
  const processingQuery = useQuery(reviewProcessingQuery(actingAsProfileId));
  const [shown, setShown] = useState<{ profile?: string; processing?: ReviewProcessing }>(() => ({
    profile: actingAsProfileId,
    processing: processingQuery.data,
  }));
  const [justFinished, setJustFinished] = useState(false);
  const last = useRef({ profile: actingAsProfileId, busy: isReviewProcessingBusy(processingQuery.data) });
  const finishedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(finishedTimer.current), []);
  useEffect(() => {
    const data = processingQuery.data;
    const profile = actingAsProfileId;
    if (last.current.profile !== profile) {
      // Another profile: its own state, no carried-over "finished".
      last.current = { profile, busy: isReviewProcessingBusy(data) };
      setShown({ profile, processing: data });
      setJustFinished(false);
      return;
    }
    if (!data) return;
    const busy = isReviewProcessingBusy(data);
    if (!busy && last.current.busy) {
      last.current.busy = false;
      let cancelled = false;
      void client
        .fetchQuery({ ...reviewListQuery(profile), staleTime: 0 })
        .catch(() => undefined)
        .then(() => {
          if (cancelled) return;
          setShown({ profile, processing: data });
          setJustFinished(true);
          clearTimeout(finishedTimer.current);
          finishedTimer.current = setTimeout(() => setJustFinished(false), FINISHED_SUMMARY_MS);
          void invalidateRecords(client, profile);
          void client.invalidateQueries({ queryKey: patientKeys.medications(profile) });
          void client.invalidateQueries({ queryKey: reviewCountQuery(profile).queryKey });
        });
      return () => {
        cancelled = true;
      };
    }
    last.current.busy = busy;
    setShown({ profile, processing: data });
    if (busy) setJustFinished(false);
  }, [processingQuery.data, actingAsProfileId, client]);
  // The "checking your new records" card belongs to the health queue only.
  const processing = queue === "health" && shown.profile === actingAsProfileId ? shown.processing ?? null : null;
  // The list never waits for the progress check: if that check is slow (or
  // fails), the queue still shows, and the card slots in on top once it
  // answers. Waiting on both made the page only as fast as the slower one.

  const afterChange = useCallback(() => void invalidatePatientData(client, actingAsProfileId), [client, actingAsProfileId]);

  const skip = (itemId: string) => {
    submit.mutate(
      { itemId, answer: "SKIPPED" },
      {
        onSuccess: () => toastSuccess(SKIPPED_MESSAGE),
        onError: (skipError) => {
          const { code, message } = skipError as { code?: string; message?: string };
          if (code !== "ITEM_NOT_AWAITING_INPUT") toastError(message ?? "Something went wrong. Please try again.");
        },
      },
    );
  };

  const openTrigger = items?.find((i) => i.id === openReviewId)?.triggerType;
  const modalCopy = (openTrigger && MODAL_COPY[openTrigger]) || MEDICATION_COPY;

  if (error && !items) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center py-24 text-center">
        <p className="text-sm font-semibold text-[#F36F8F]">{error}</p>
      </div>
    );
  }
  if (!items) return <PageSpinner className="bg-[#F7F7F7]" />;

  return (
    <>
      <HumanReviewQueueSection
        items={items}
        focusItemId={focusItemId}
        processing={processing}
        justFinished={queue === "health" && justFinished}
        onReview={setOpenReviewId}
        onSkip={skip}
        skippingId={skippingId}
        onBulkReview={(tab) => navigate(bulkReviewPath(queue, undefined, tab))}
        openCount={countAwaiting(items, queue)}
      />
      <AppModal
        open={openReviewId !== null}
        onOpenChange={(open) => { if (!open) setOpenReviewId(null); }}
        title={<span className="text-[#0A2947]">{modalCopy.title}</span>}
        description={<span className="text-[#4B5563]">{modalCopy.description}</span>}
        width={860}
        footer={null}
      >
        {openReviewId && (
          <ReviewItemView
            key={`${actingAsProfileId ?? "self"}:${openReviewId}`}
            variant="modal"
            itemId={openReviewId}
            actingAsProfileId={actingAsProfileId}
            onBack={() => setOpenReviewId(null)}
            onItemChanged={afterChange}
          />
        )}
      </AppModal>
    </>
  );
}
